import { useMemo } from 'react';
import { CalendarNavigationConfig, CalendarGrid } from '@frontend/calendar-grid';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Button, ContentLoader, Heading } from '@frontend/design-system';
import { useGetWeeklyAvailabilitySlots } from './use-get-weekly-availability-slots';
import { AvailabilityHeader } from './week-view-availability-header/availability-header';
import { useAvailabilityFilters } from './week-view-availability-header/use-availability-filters';

const Loader = () => <ContentLoader show />;
const NoAvailability = ({ onJumpToNextWeek }: { onJumpToNextWeek: () => void }) => {
  const { t } = useTranslation('schedule');
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: theme.spacing(4),
      }}
    >
      <Heading as={'h3'} level={3} css={{ marginBottom: theme.spacing(3) }}>
        {t('No slots available')}
      </Heading>
      <Button variant='primary' onClick={onJumpToNextWeek} iconName='calendar'>
        {t('Jump to next week')}
      </Button>
    </div>
  );
};

export const ScheduleAvailabilityPage = ({ appointmentTypeId }: { appointmentTypeId?: string }) => {
  const { t } = useTranslation('schedule');

  const { selectedOrgId, selectedLocationIds } = useAppScopeStore();
  const selectedLocationId = selectedLocationIds[0];

  const { resolvedFilters, headerFieldProps } = useAvailabilityFilters({
    locationId: selectedLocationId,
    orgId: selectedOrgId,
    initialServiceId: appointmentTypeId,
  });

  const { columns, events, isLoading } = useGetWeeklyAvailabilitySlots({
    availabilityFilters: resolvedFilters,
    orgId: selectedOrgId,
    locationId: selectedLocationId,
  });

  const navigationConfig = useMemo<CalendarNavigationConfig>(() => {
    return {
      canGoNext: true,
      canGoPrevious: !headerFieldProps.isCurrentWeek,
      onNext: headerFieldProps.goToNextWeek,
      onPrevious: headerFieldProps.goToPreviousWeek,
    };
  }, [headerFieldProps.goToNextWeek, headerFieldProps.goToPreviousWeek, headerFieldProps.isCurrentWeek]);

  return (
    <section css={styles.container}>
      <Heading as='h3' level={3} css={{ margin: theme.spacing(2) }}>
        {t('Availability Finder')}
      </Heading>
      <AvailabilityHeader locationId={selectedLocationId} orgId={selectedOrgId} {...headerFieldProps} />
      <section css={styles.gridSection}>
        <CalendarGrid
          columnBodyVariant='stacked'
          headerType='weekday'
          events={isLoading ? [] : events}
          columns={columns}
          NoEventsComponent={
            isLoading
              ? Loader
              : !events.length
              ? () => <NoAvailability onJumpToNextWeek={headerFieldProps.goToNextWeek} />
              : undefined
          }
          navigationConfig={navigationConfig}
        />
      </section>
    </section>
  );
};

const styles = {
  container: {
    marginRight: theme.spacing(-2),
    height: '100%',
    minHeight: 0,
    borderRadius: theme.borderRadius.small,
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
  },
  gridSection: {
    flex: 1,
    minHeight: 0,
  },
};
